import React, { useState, useEffect } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import PlayerColumn from './PlayerColumn';
import PlayerCard from './PlayerCard';
import type { Player } from '../services/api';
import { getPlayers, updatePlayer } from '../services/api';
import eventBus from '../utils/eventBus';
import { Box, Typography, GridLegacy as Grid, Paper, Alert, CircularProgress } from '@mui/material';

export type PlayerStatus = 'available' | 'on_court' | 'unavailable' | 'absent';

const PlayerKanban: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [columns, setColumns] = useState<Record<PlayerStatus, Player[]>>({
    on_court: [],
    available: [],
    unavailable: [],
    absent: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePlayer, setActivePlayer] = useState<Player | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const fetchAndSetPlayers = async () => {
    try {
      setLoading(true);
      const allPlayers = await getPlayers();
      setPlayers(allPlayers);
      
      const newColumns: Record<PlayerStatus, Player[]> = {
        on_court: [],
        available: [],
        unavailable: [],
        absent: [],
      };

      for (const player of allPlayers) {
        const status = player.status as PlayerStatus;
        if (newColumns[status]) {
          newColumns[status].push(player);
        } else {
          newColumns.unavailable.push(player);
        }
      }
      setColumns(newColumns);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch players for kanban", err);
      setError("載入球員資料失敗。");
    } finally {
      setLoading(false);
    }
  };


  // Define a type for the event payload for clarity
  type StatusChangePayload = {
    players: Player[];
    newStatus: PlayerStatus;
  };

  const handlePlayersStatusChanged = (payload: unknown) => {
    // Type guard to ensure payload is what we expect
    const isPayloadValid = (p: any): p is StatusChangePayload => 
      p && Array.isArray(p.players) && typeof p.newStatus === 'string';

    if (!isPayloadValid(payload)) {
      console.warn('Invalid payload for playersStatusChanged event, falling back to refetch:', payload);
      fetchAndSetPlayers();
      return;
    }
    
    setColumns(currentColumns => {
      const newColumns = JSON.parse(JSON.stringify(currentColumns)); // Deep copy
      const playersToMove = payload.players;
      const newStatus = payload.newStatus;
      const playerIdsToMove = new Set(playersToMove.map(p => p.id));

      // Remove players from their old columns
      for (const status in newColumns) {
        newColumns[status as PlayerStatus] = newColumns[status as PlayerStatus].filter(
          (p: Player) => !playerIdsToMove.has(p.id)
        );
      }

      // Add players to their new column, avoiding duplicates
      if (newColumns[newStatus]) {
        const existingIds = new Set(newColumns[newStatus].map((p: Player) => p.id));
        const playersToAdd = playersToMove
          .filter(p => !existingIds.has(p.id))
          .map(p => ({ ...p, status: newStatus }));

        newColumns[newStatus].push(...playersToAdd);
      }
      
      return newColumns;
    });
  };

  useEffect(() => {
    // Initial fetch
    fetchAndSetPlayers();
    
    // Listen for optimistic updates
    eventBus.on('playersStatusChanged', handlePlayersStatusChanged);
    
    // Fallback listener for any other generic data changes
    eventBus.on('dataChanged', fetchAndSetPlayers);

    return () => {
      eventBus.remove('playersStatusChanged', handlePlayersStatusChanged);
      eventBus.remove('dataChanged', fetchAndSetPlayers);
    };
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const player = players.find(p => p.id === active.id);
    if (player) {
      setActivePlayer(player);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActivePlayer(null);
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id; 

    const activeContainer = findContainer(activeId);
    const overContainer = overId as PlayerStatus;

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    const movedPlayer = players.find(p => p.id === activeId);
    if (!movedPlayer) return;

    const newColumns = { ...columns };
    newColumns[activeContainer] = newColumns[activeContainer].filter(p => p.id !== activeId);
    newColumns[overContainer] = [...newColumns[overContainer], { ...movedPlayer, status: overContainer }];
    setColumns(newColumns);

    try {
      await updatePlayer(movedPlayer.id, { status: overContainer });
    } catch (err) {
      console.error("Failed to update player status", err);
      setError("更新球員狀態失敗，正在還原操作。");
      // Revert optimistic update
      setColumns(columns);
    }
  };

  const findContainer = (id: number | string) => {
    for (const key in columns) {
      if (columns[key as PlayerStatus].some(p => p.id === id)) {
        return key as PlayerStatus;
      }
    }
    return null;
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        球員狀態看板
      </Typography>
      <Paper elevation={2} sx={{ p: 2, borderRadius: '12px' }}>
        <DndContext 
          sensors={sensors}
          onDragStart={handleDragStart} 
          onDragEnd={handleDragEnd}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <PlayerColumn id="on_court" title="比賽中" players={columns.on_court} isDropDisabled />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <PlayerColumn id="available" title="可上場" players={columns.available} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <PlayerColumn id="unavailable" title="休息中" players={columns.unavailable} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <PlayerColumn id="absent" title="缺席" players={columns.absent} />
            </Grid>
          </Grid>
          <DragOverlay>
            {activePlayer ? <PlayerCard player={activePlayer} isDragging /> : null}
          </DragOverlay>
        </DndContext>
      </Paper>
    </Box>
  );
};

export default PlayerKanban;